/**
 * markdownDoc — markdown ↔ ProseMirror document converter for the
 * Obsidian Vault editor.
 *
 * Scope (C.6): headings, paragraphs, hard breaks, bullet lists, ordered
 * lists, code fences, horizontal rules, blockquotes, plus our four
 * Obsidian extensions (WikilinkMark, EmbedBlock, ObsidianTag,
 * CalloutBlock). Bold (`**x**`) and italic (`*x*`) are passed through
 * starter-kit's marks. Round-trip is "close enough" — pure markdown
 * formatting variations (e.g. `*` vs `-` bullets) are normalized.
 *
 * We intentionally do not depend on `remark-stringify` for the writer:
 * remark's output doesn't know about wikilinks/embeds/callouts and
 * would strip them. The writer here mirrors the reader, so a note that
 * was opened and immediately saved produces a byte-identical body
 * (modulo bullet-style normalization).
 */

import { tokenizeWikilinks, type WikilinkAttrs } from './WikilinkMark';
import { tokenizeEmbeds, type EmbedAttrs } from './EmbedBlock';
import { tokenizeTags } from './ObsidianTag';
import { extractCallouts } from './CalloutBlock';

// ---------------------------------------------------------------------------
// ProseMirror JSON minimal types
// ---------------------------------------------------------------------------

export interface PMTextNode {
  type: 'text';
  text: string;
  marks?: PMMark[];
}
export type PMAttrs = Record<string, unknown>;

export interface PMMark {
  type: string;
  attrs?: PMAttrs;
}

function asAttrs(v: object | undefined | null): PMAttrs | undefined {
  if (!v) return undefined;
  return v as unknown as PMAttrs;
}
export interface PMInline {
  type: string;
  text?: string;
  marks?: PMMark[];
  attrs?: Record<string, unknown>;
  content?: PMInline[];
}
export interface PMBlock {
  type: string;
  attrs?: Record<string, unknown>;
  content?: (PMBlock | PMInline)[];
}
export interface PMDoc {
  type: 'doc';
  // Tiptap's `editor.getJSON()` returns `{type:'doc'}` with no
  // `content` array when the editor is empty (initial mount before
  // setContent fires). Marking optional matches that reality so
  // callers can't deref `.content.map(...)` without a null check.
  content?: PMBlock[];
}

// ---------------------------------------------------------------------------
// Reader: markdown → ProseMirror JSON
// ---------------------------------------------------------------------------

const BOLD = /\*\*([^*\n]+?)\*\*/g;
const ITALIC = /(?<!\*)\*([^*\n]+?)\*(?!\*)/g;
const CODE_INLINE = /`([^`\n]+?)`/g;
const LINK = /\[([^\]\n]+?)\]\(([^)\n]+?)\)/g;

interface SpanRange {
  start: number;
  end: number;
  build: (inner: string) => PMInline;
}

function buildInlines(text: string): PMInline[] {
  // Pass 1: wikilinks.
  const wikilinkParts = tokenizeWikilinks(text);
  const out: PMInline[] = [];
  for (const wp of wikilinkParts) {
    if (wp.kind === 'wikilink') {
      const attrs = wp.attrs as WikilinkAttrs;
      out.push({
        type: 'text',
        text: wp.value,
        marks: [{ type: 'wikilink', attrs: asAttrs(attrs) }],
      });
      continue;
    }
    // Pass 2 on the plain segment: embeds.
    const embedParts = tokenizeEmbeds(wp.value);
    for (const ep of embedParts) {
      if (ep.kind === 'embed') {
        // Embeds are block atoms; in inline position we still emit them
        // as inline content but TipTap may auto-lift them to block. For
        // first cut we represent them as `text` with a sentinel mark; a
        // proper inline embed will arrive in C.6+.
        const attrs = ep.attrs as EmbedAttrs;
        out.push({
          type: 'text',
          text: `![[${attrs.target}${attrs.section ? `#${attrs.section}` : ''}]]`,
          marks: [{ type: 'embedInline', attrs: asAttrs(attrs) }],
        });
        continue;
      }
      // Pass 3 on the still-plain segment: tags.
      const tagParts = tokenizeTags(ep.value);
      for (const tp of tagParts) {
        if (tp.kind === 'tag') {
          out.push({
            type: 'text',
            text: tp.value,
            marks: [{ type: 'obsidianTag', attrs: asAttrs(tp.attrs) }],
          });
          continue;
        }
        // Pass 4 on the plain text: bold/italic/inline-code.
        const spans = parseRichSpans(tp.value);
        for (const span of spans) out.push(span);
      }
    }
  }
  // Collapse adjacent text nodes that share marks (cosmetic — keeps the
  // PM doc terse).
  return collapseAdjacentText(out);
}

function parseRichSpans(text: string): PMInline[] {
  const ranges: SpanRange[] = [];
  let m: RegExpExecArray | null;
  BOLD.lastIndex = 0;
  while ((m = BOLD.exec(text)) !== null) {
    const inner = m[1]!;
    ranges.push({
      start: m.index,
      end: m.index + m[0].length,
      build: () => ({ type: 'text', text: inner, marks: [{ type: 'bold' }] }),
    });
  }
  ITALIC.lastIndex = 0;
  while ((m = ITALIC.exec(text)) !== null) {
    if (ranges.some((r) => m!.index >= r.start && m!.index < r.end)) continue;
    const inner = m[1]!;
    ranges.push({
      start: m.index,
      end: m.index + m[0].length,
      build: () => ({ type: 'text', text: inner, marks: [{ type: 'italic' }] }),
    });
  }
  CODE_INLINE.lastIndex = 0;
  while ((m = CODE_INLINE.exec(text)) !== null) {
    if (ranges.some((r) => m!.index >= r.start && m!.index < r.end)) continue;
    const inner = m[1]!;
    ranges.push({
      start: m.index,
      end: m.index + m[0].length,
      build: () => ({ type: 'text', text: inner, marks: [{ type: 'code' }] }),
    });
  }
  LINK.lastIndex = 0;
  while ((m = LINK.exec(text)) !== null) {
    if (ranges.some((r) => m!.index >= r.start && m!.index < r.end)) continue;
    const innerText = m[1]!;
    const href = m[2]!;
    ranges.push({
      start: m.index,
      end: m.index + m[0].length,
      build: () => ({
        type: 'text',
        text: innerText,
        marks: [{ type: 'link', attrs: { href } }],
      }),
    });
  }
  ranges.sort((a, b) => a.start - b.start);
  const out: PMInline[] = [];
  let cursor = 0;
  for (const r of ranges) {
    if (cursor < r.start) {
      out.push({ type: 'text', text: text.slice(cursor, r.start) });
    }
    out.push(r.build(text.slice(r.start, r.end)));
    cursor = r.end;
  }
  if (cursor < text.length) {
    out.push({ type: 'text', text: text.slice(cursor) });
  }
  if (out.length === 0) out.push({ type: 'text', text });
  return out;
}

function collapseAdjacentText(parts: PMInline[]): PMInline[] {
  const out: PMInline[] = [];
  for (const p of parts) {
    const prev = out[out.length - 1];
    if (
      p.type === 'text' &&
      prev?.type === 'text' &&
      sameMarks(p.marks, prev.marks)
    ) {
      prev.text = `${prev.text ?? ''}${p.text ?? ''}`;
    } else {
      out.push(p);
    }
  }
  // Drop empty text nodes (PM dislikes them).
  return out.filter((p) => !(p.type === 'text' && (p.text ?? '') === ''));
}

function sameMarks(a?: PMMark[], b?: PMMark[]): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]!.type !== b[i]!.type) return false;
    if (JSON.stringify(a[i]!.attrs ?? {}) !== JSON.stringify(b[i]!.attrs ?? {})) return false;
  }
  return true;
}

function parseBlocksFromLines(lines: string[]): PMBlock[] {
  const out: PMBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === '') {
      i++;
      continue;
    }
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.startsWith('```')) {
        code.push(lines[i]!);
        i++;
      }
      i++;
      // C.13a — route ```dataview``` fences to a dedicated atom node so
      // the TipTap node view can render the table inline. The block
      // round-trips byte-faithfully via the `dataviewBlock` branch in
      // renderBlock below.
      if (lang === 'dataview') {
        out.push({
          type: 'dataviewBlock',
          attrs: { source: code.join('\n') },
        });
        continue;
      }
      out.push({
        type: 'codeBlock',
        attrs: lang ? { language: lang } : {},
        content: [{ type: 'text', text: code.join('\n') }],
      });
      continue;
    }
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      out.push({
        type: 'heading',
        attrs: { level: heading[1]!.length },
        content: buildInlines(heading[2]!),
      });
      i++;
      continue;
    }
    if (/^---+\s*$/.test(line)) {
      out.push({ type: 'horizontalRule' });
      i++;
      continue;
    }
    if (/^[-*+]\s+/.test(line)) {
      const items: PMBlock[] = [];
      while (i < lines.length && /^[-*+]\s+/.test(lines[i]!)) {
        const text = lines[i]!.replace(/^[-*+]\s+/, '');
        items.push({
          type: 'listItem',
          content: [{ type: 'paragraph', content: buildInlines(text) }],
        });
        i++;
      }
      out.push({ type: 'bulletList', content: items });
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      const items: PMBlock[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i]!)) {
        const text = lines[i]!.replace(/^\d+\.\s+/, '');
        items.push({
          type: 'listItem',
          content: [{ type: 'paragraph', content: buildInlines(text) }],
        });
        i++;
      }
      out.push({ type: 'orderedList', content: items });
      continue;
    }
    // Standalone block-level embed (line that is *only* `![[…]]`).
    const standaloneEmbed = line.match(/^!\[\[([^\]\n#]+?)(?:#([^\]\n]+))?\]\]\s*$/);
    if (standaloneEmbed) {
      out.push({
        type: 'embed',
        attrs: {
          target: standaloneEmbed[1]!.trim(),
          section: standaloneEmbed[2]?.trim() ?? null,
        },
      });
      i++;
      continue;
    }
    // Paragraph: collect until blank line / structural change.
    const para: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i]!.trim() !== '' &&
      !lines[i]!.startsWith('```') &&
      !/^#{1,6}\s+/.test(lines[i]!) &&
      !/^---+\s*$/.test(lines[i]!) &&
      !/^[-*+]\s+/.test(lines[i]!) &&
      !/^\d+\.\s+/.test(lines[i]!)
    ) {
      para.push(lines[i]!);
      i++;
    }
    out.push({
      type: 'paragraph',
      content: buildInlines(para.join(' ')),
    });
  }
  return out;
}

/**
 * Convert a markdown body (frontmatter already stripped) to a ProseMirror JSON doc.
 */
export function markdownToDoc(body: string): PMDoc {
  const chunks = extractCallouts(body);
  const content: PMBlock[] = [];
  for (const chunk of chunks) {
    if (chunk.kind === 'callout') {
      content.push({
        type: 'callout',
        attrs: {
          calloutKind: chunk.calloutKind ?? 'note',
          calloutTitle: chunk.calloutTitle ?? '',
        },
        content: parseBlocksFromLines(chunk.lines),
      });
    } else {
      const blocks = parseBlocksFromLines(chunk.lines);
      content.push(...blocks);
    }
  }
  return { type: 'doc', content };
}

// ---------------------------------------------------------------------------
// Writer: ProseMirror JSON → markdown
// ---------------------------------------------------------------------------

function renderInlines(content: PMInline[] | undefined): string {
  if (!content) return '';
  let out = '';
  for (const node of content) {
    if (node.type !== 'text') continue;
    let text = node.text ?? '';
    const marks = node.marks ?? [];
    const wikilink = marks.find((m) => m.type === 'wikilink');
    if (wikilink) {
      const attrs = (wikilink.attrs ?? {}) as unknown as WikilinkAttrs;
      const target = attrs.target ?? text;
      const alias = attrs.alias ?? null;
      const section = attrs.section ?? null;
      const suffix = section ? `#${section}` : '';
      out += `[[${target}${suffix}${alias ? `|${alias}` : ''}]]`;
      continue;
    }
    const embedInline = marks.find((m) => m.type === 'embedInline');
    if (embedInline) {
      const attrs = (embedInline.attrs ?? {}) as unknown as EmbedAttrs;
      out += `![[${attrs.target}${attrs.section ? `#${attrs.section}` : ''}]]`;
      continue;
    }
    if (marks.find((m) => m.type === 'obsidianTag')) {
      out += text; // already includes leading '#'
      continue;
    }
    if (marks.find((m) => m.type === 'code')) text = `\`${text}\``;
    if (marks.find((m) => m.type === 'bold')) text = `**${text}**`;
    if (marks.find((m) => m.type === 'italic')) text = `*${text}*`;
    const link = marks.find((m) => m.type === 'link');
    if (link) {
      const href = (link.attrs as { href?: string } | undefined)?.href ?? '';
      text = `[${text}](${href})`;
    }
    out += text;
  }
  return out;
}

function renderBlock(b: PMBlock | PMInline, indent = ''): string {
  switch (b.type) {
    case 'heading': {
      const level = (b.attrs?.level as number) ?? 1;
      return `${'#'.repeat(level)} ${renderInlines((b as PMBlock).content as PMInline[])}`;
    }
    case 'paragraph':
      return renderInlines((b as PMBlock).content as PMInline[]);
    case 'horizontalRule':
      return '---';
    case 'codeBlock': {
      const lang = (b.attrs?.language as string) ?? '';
      const inner = ((b as PMBlock).content as PMInline[]) ?? [];
      const text = inner.map((c) => c.text ?? '').join('');
      return `\`\`\`${lang}\n${text}\n\`\`\``;
    }
    case 'dataviewBlock': {
      // C.13a — inverse of the parser branch above. The atom node stores
      // the DQL source verbatim in attrs.source; we re-emit it back as a
      // ```dataview fence so the file on disk is unchanged by edits.
      const source = (b.attrs?.source as string) ?? '';
      return `\`\`\`dataview\n${source}\n\`\`\``;
    }
    case 'bulletList': {
      const items = ((b as PMBlock).content as PMBlock[]) ?? [];
      return items
        .map((it) => {
          const inner =
            ((it.content as PMBlock[]) ?? [])
              .map((c) => renderBlock(c))
              .join('\n')
              .replace(/^/gm, '');
          return `- ${inner}`;
        })
        .join('\n');
    }
    case 'orderedList': {
      const items = ((b as PMBlock).content as PMBlock[]) ?? [];
      return items
        .map((it, idx) => {
          const inner = ((it.content as PMBlock[]) ?? [])
            .map((c) => renderBlock(c))
            .join('\n');
          return `${idx + 1}. ${inner}`;
        })
        .join('\n');
    }
    case 'listItem': {
      const inner = ((b as PMBlock).content as PMBlock[]) ?? [];
      return inner.map((c) => renderBlock(c)).join('\n');
    }
    case 'embed': {
      const attrs = (b.attrs ?? {}) as unknown as EmbedAttrs;
      return `![[${attrs.target}${attrs.section ? `#${attrs.section}` : ''}]]`;
    }
    case 'callout': {
      const attrs = (b.attrs ?? {}) as CalloutAttrsLike;
      const inner = ((b as PMBlock).content as PMBlock[]) ?? [];
      const body = inner
        .map((c) => renderBlock(c, indent))
        .join('\n\n')
        .split('\n')
        .map((l) => `> ${l}`)
        .join('\n');
      return `> [!${attrs.calloutKind ?? 'note'}] ${attrs.calloutTitle ?? ''}\n${body}`;
    }
    default:
      return '';
  }
}

interface CalloutAttrsLike {
  calloutKind?: string;
  calloutTitle?: string;
}

/**
 * Convert a ProseMirror JSON doc back to markdown. Round-trips with
 * `markdownToDoc` modulo whitespace normalization (single blank line
 * between blocks; bullet style is always `- `).
 */
export function docToMarkdown(doc: PMDoc): string {
  // Empty Tiptap doc → no `content` array. Treat as empty markdown
  // instead of crashing in the .map call. The PreviewTipTapEditor's
  // mount effect calls docToMarkdown before its first setContent.
  const content = doc.content ?? [];
  const blocks = content.map((b) => renderBlock(b));
  return blocks.join('\n\n').trim() + '\n';
}
